import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { Loader } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import "./EarningsBreakdownPage.css";

// ── Constants ──────────────────────────────────────────────────────────────

const PERIODS = [
  { key: "month", label: "This Month", short: "MTD" },
  { key: "quarter", label: "This Quarter", short: "QTD" },
  { key: "ytd", label: "Year to Date", short: "YTD" },
  { key: "all", label: "All Time", short: "TOTAL" },
];

const EARNINGS_VIEWS = [
  { key: "mine", label: "MY EARNINGS", colHead: "MY EARN" },
  { key: "rep", label: "REP EARNINGS", colHead: "REP EARN" },
  { key: "managers", label: "MANAGERS", colHead: "MGR EARN" },
];

const AVATAR_COLORS = [
  "#7a9a3c",
  "#8b5e3c",
  "#7b5ea7",
  "#3c8b5e",
  "#3c5ea7",
  "#a7663c",
  "#5e3ca7",
  "#a73c5e",
];

// ── Helpers ────────────────────────────────────────────────────────────────

function getPeriodDates(key) {
  const now = new Date();
  const fmt = d => d.toISOString().slice(0, 10);
  if (key === "all") return {};
  if (key === "month") {
    return {
      start_date: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
      end_date: fmt(now),
    };
  }
  if (key === "quarter") {
    const qStartMonth = Math.floor(now.getMonth() / 3) * 3;
    return {
      start_date: fmt(new Date(now.getFullYear(), qStartMonth, 1)),
      end_date: fmt(now),
    };
  }
  // ytd
  return {
    start_date: fmt(new Date(now.getFullYear(), 0, 1)),
    end_date: fmt(now),
  };
}

function fmtMoney(n) {
  if (!n) return "$0";
  if (n >= 1_000_000)
    return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 10_000) return `$${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return `$${Math.round(n)}`;
}

function fmtPct(v) {
  if (!v) return "—";
  const pct = Math.abs(v) <= 1 ? v * 100 : v;
  return pct === Math.round(pct) ? `${Math.round(pct)}%` : `${pct.toFixed(1)}%`;
}

function getInitials(firstName = "", lastName = "") {
  return `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase() || "?";
}

function getAvatarColor(id) {
  let h = (id * 2654435761) >>> 0;
  h ^= h >> 13;
  h = (h * 2246822519) >>> 0;
  h ^= h >> 16;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function buildRepIndex(reps, index = {}) {
  for (const rep of reps) {
    index[rep.rep_id] = rep;
    if (rep.reps?.length) buildRepIndex(rep.reps, index);
  }
  return index;
}

function countSubtree(rep) {
  if (!rep.reps?.length) return 0;
  return rep.reps.reduce((sum, r) => sum + 1 + countSubtree(r), 0);
}

function getCommission(row, view) {
  if (!row) return { margin: 0, projMargin: 0, earnings: 0, projEarnings: 0 };
  if (view === "rep") {
    return {
      margin: row.user_commission_percentage ?? 0,
      projMargin: row.user_projected_commission_percentage ?? 0,
      earnings: row.user_commission_earnings ?? 0,
      projEarnings: row.user_projected_commission_earnings ?? 0,
    };
  }
  if (view === "mine") {
    return {
      margin: row.current_manager_percentage ?? 0,
      projMargin: row.projected_manager_percentage ?? 0,
      earnings: row.current_manager_earnings ?? 0,
      projEarnings: row.projected_manager_earnings ?? 0,
    };
  }
  // managers — prefer MD cut, fall back to override
  const mdEarnings = row.current_md_earnings ?? 0;
  return {
    margin:
      mdEarnings > 0
        ? (row.current_md_percentage ?? 0)
        : (row.current_override_percentage ?? 0),
    projMargin:
      mdEarnings > 0
        ? (row.projected_md_percentage ?? 0)
        : (row.projected_override_percentage ?? 0),
    earnings:
      mdEarnings > 0 ? mdEarnings : (row.current_override_earnings ?? 0),
    projEarnings:
      mdEarnings > 0
        ? (row.projected_md_earnings ?? 0)
        : (row.projected_override_earnings ?? 0),
  };
}

// ── Component ──────────────────────────────────────────────────────────────

function EarningsBreakdownPage() {
  const { getVerifiedJWT } = useAuth();

  const [period, setPeriod] = useState("ytd");
  const [earningsView, setEarningsView] = useState("mine");
  const [periodOpen, setPeriodOpen] = useState(false);

  const [topLevelReps, setTopLevelReps] = useState([]);
  const [repIndex, setRepIndex] = useState({});
  const [leaderboardByRepId, setLeaderboardByRepId] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // drill-in path: array of rep_ids
  const [path, setPath] = useState([]);

  // ── data fetching ──

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const dates = getPeriodDates(period);
      const params = new URLSearchParams(dates).toString();

      const [downlineRes, lbRes] = await Promise.all([
        fetch(getApiEndpoint("/v1/company-downline/reps-by-manager"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({}),
        }),
        fetch(
          getApiEndpoint(
            `/v1/revenue/leaderboard${params ? `?${params}` : ""}`
          ),
          {
            headers: { Authorization: `Bearer ${jwt}` },
          }
        ),
      ]);

      if (!downlineRes.ok) throw new Error("Failed to load downline");
      if (!lbRes.ok) throw new Error("Failed to load earnings data");

      const [downlineData, lbData] = await Promise.all([
        downlineRes.json(),
        lbRes.json(),
      ]);

      const reps = downlineData.data?.reps ?? [];
      setTopLevelReps(reps);
      setRepIndex(buildRepIndex(reps));

      const rows = lbData.data?.rows ?? [];
      const lbMap = {};
      for (const row of rows) {
        const rid = row.rep_id ?? row.reppi_user_id;
        if (rid) lbMap[rid] = row;
      }
      setLeaderboardByRepId(lbMap);
    } catch (err) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT, period]);

  useEffect(() => {
    load();
  }, [load]);

  // ── derived state ──

  const isRoot = path.length === 0;
  const focusId = isRoot ? null : path[path.length - 1];

  const focusRep = useMemo(
    () => (focusId ? repIndex[focusId] : null),
    [focusId, repIndex]
  );

  const currentReps = useMemo(
    () => (isRoot ? topLevelReps : (focusRep?.reps ?? [])),
    [isRoot, topLevelReps, focusRep]
  );

  const focusName = isRoot
    ? "My Company"
    : `${focusRep?.first_name ?? ""} ${focusRep?.last_name ?? ""}`.trim();

  const periodDef = PERIODS.find(p => p.key === period) ?? PERIODS[2];
  const earningsViewDef =
    EARNINGS_VIEWS.find(v => v.key === earningsView) ?? EARNINGS_VIEWS[0];

  const summaryTotals = useMemo(() => {
    let totalEarnings = 0,
      projEarnings = 0;
    const teamCount = currentReps.reduce(
      (sum, r) => sum + 1 + countSubtree(r),
      0
    );
    for (const rep of currentReps) {
      const c = getCommission(leaderboardByRepId[rep.rep_id], earningsView);
      totalEarnings += c.earnings;
      projEarnings += c.projEarnings;
    }
    return { teamCount, totalEarnings, projEarnings };
  }, [currentReps, leaderboardByRepId, earningsView]);

  // ── handlers ──

  const handleRepClick = rep => {
    if (!rep.reps?.length) return;
    setPath(prev => [...prev, rep.rep_id]);
    setPeriodOpen(false);
  };

  const handleBreadcrumbClick = index => {
    if (index === -1) setPath([]);
    else setPath(prev => prev.slice(0, index + 1));
  };

  const handlePeriodChange = key => {
    setPeriod(key);
    setPeriodOpen(false);
    setPath([]);
  };

  const handlePageClick = () => setPeriodOpen(false);

  // ── render ──

  if (loading) {
    return (
      <div className="eb-loading">
        <Loader size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="eb-error">
        <p>{error}</p>
        <button className="eb-retry-btn" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="eb-page" onClick={handlePageClick}>
      {/* Focus card */}
      <div className="eb-focus-card">
        <div className="eb-focus-left">
          <div
            className="eb-focus-avatar"
            style={{ background: getAvatarColor(focusId ?? 0) }}
          >
            {isRoot
              ? "MC"
              : getInitials(focusRep?.first_name, focusRep?.last_name)}
          </div>
          <div>
            <div className="eb-focus-name">{focusName}</div>
            <div className="eb-focus-sub">{periodDef.short} · TEAM ROLL-UP</div>
          </div>
        </div>
        <div className="eb-focus-right">
          <div className="eb-focus-count">{currentReps.length}</div>
          <div className="eb-focus-count-label">DIRECT DOWNLINE</div>
        </div>
      </div>

      {/* Summary stats strip */}
      <div className="eb-stats">
        <div className="eb-stat">
          <div className="eb-stat-value">{summaryTotals.teamCount}</div>
          <div className="eb-stat-label">TEAM</div>
        </div>
        <div className="eb-stat">
          <div className="eb-stat-value accent">
            {fmtMoney(summaryTotals.totalEarnings)}
          </div>
          <div className="eb-stat-label">{periodDef.short}</div>
        </div>
        <div className="eb-stat">
          <div className="eb-stat-value">
            {fmtMoney(summaryTotals.projEarnings)}
          </div>
          <div className="eb-stat-label">PROJ.</div>
        </div>
      </div>

      {/* Breadcrumb */}
      {!isRoot && (
        <div className="eb-breadcrumb">
          <button
            className="eb-crumb-btn"
            onClick={() => handleBreadcrumbClick(-1)}
          >
            My Company
          </button>
          {path.map((repId, i) => {
            const rep = repIndex[repId];
            const name = rep
              ? `${rep.first_name ?? ""} ${rep.last_name ?? ""}`.trim()
              : String(repId);
            const isCurrent = i === path.length - 1;
            return (
              <React.Fragment key={repId}>
                <span className="eb-crumb-sep">
                  <IconChevronRight size={11} />
                </span>
                <button
                  className={`eb-crumb-btn${isCurrent ? " current" : ""}`}
                  onClick={() => !isCurrent && handleBreadcrumbClick(i)}
                >
                  {name.split(" ")[0]}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Filter + toggle row */}
      <div className="eb-controls" onClick={e => e.stopPropagation()}>
        {/* Period selector */}
        <div className="eb-filter-pill-wrap">
          <button
            className={`eb-filter-pill${periodOpen ? " open" : ""}`}
            onClick={() => setPeriodOpen(v => !v)}
          >
            <span>{periodDef.label.toUpperCase()}</span>
            <span className="eb-pill-caret">{periodOpen ? "▲" : "▼"}</span>
          </button>
          {periodOpen && (
            <div className="eb-dropdown">
              {PERIODS.map(p => (
                <button
                  key={p.key}
                  className={`eb-dropdown-row${p.key === period ? " selected" : ""}`}
                  onClick={() => handlePeriodChange(p.key)}
                >
                  <span>{p.label.toUpperCase()}</span>
                  {p.key === period && <span className="eb-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Earnings view toggle */}
        <div className="eb-toggle">
          {EARNINGS_VIEWS.map(v => (
            <button
              key={v.key}
              className={`eb-toggle-btn${earningsView === v.key ? " active" : ""}`}
              onClick={() => setEarningsView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="eb-table">
        <div className="eb-col-header">
          <div className="eb-col-rep">REP</div>
          <div className="eb-col-earn-group">
            <div className="eb-col-earn">MARGIN</div>
            <div className="eb-col-earn">{earningsViewDef.colHead}</div>
          </div>
          <div className="eb-col-chevron" />
        </div>

        {currentReps.length === 0 ? (
          <div className="eb-empty">No reps in this downline</div>
        ) : (
          currentReps.map((rep, i) => {
            const name =
              `${rep.first_name ?? ""} ${rep.last_name ?? ""}`.trim() ||
              "Unknown";
            const initials = getInitials(rep.first_name, rep.last_name);
            const avatarColor = getAvatarColor(rep.rep_id ?? i);
            const hasChildren = !!rep.reps?.length;
            const directCount = rep.reps?.length ?? 0;
            const teamTotal = countSubtree(rep);
            const sub =
              directCount > 0
                ? `${directCount} DIR · ${directCount + teamTotal} TEAM`
                : "INDIVIDUAL";
            const lbRow = leaderboardByRepId[rep.rep_id];
            const c = getCommission(lbRow, earningsView);

            return (
              <div
                key={rep.rep_id ?? i}
                className={`eb-rep-row${hasChildren ? " clickable" : ""}`}
                onClick={() => hasChildren && handleRepClick(rep)}
              >
                <div className="eb-rep-info">
                  <div
                    className="eb-avatar"
                    style={{ background: avatarColor }}
                  >
                    {initials}
                  </div>
                  <div className="eb-rep-text">
                    <div className="eb-rep-name">{name}</div>
                    <div className="eb-rep-sub">{sub}</div>
                  </div>
                </div>

                <div className="eb-earn-cells">
                  {/* Margin column */}
                  <div className="eb-earn-col">
                    <div className="eb-earn-main">{fmtPct(c.margin)}</div>
                    <div className="eb-earn-sub">{fmtPct(c.projMargin)}</div>
                  </div>
                  {/* Earnings column */}
                  <div className="eb-earn-col">
                    <div
                      className={`eb-earn-main${c.earnings > 0 ? " accent" : ""}`}
                    >
                      {fmtMoney(c.earnings)}
                    </div>
                    <div className="eb-earn-sub">
                      {fmtMoney(c.projEarnings)}
                    </div>
                  </div>
                </div>

                <div className="eb-chevron-cell">
                  {hasChildren && <IconChevronRight size={13} />}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default EarningsBreakdownPage;
